import { OrderSupport } from './OrderSupport';
import { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  api,
  post,
  useData,
  useSession,
  orgID,
  apiOrigin,
  downloadPrivateFile,
} from '@yingbaba/api';
import { Button, Empty, PageTitle, Modal, ErrorNotice, Notice, Field, Select } from '@yingbaba/ui';
import {
  money,
  datetime,
  memberCommandKey,
  clearMemberCommand,
  parseMemberMoney,
  digitalProjectAssetPath,
  digitalPreviews,
} from '@yingbaba/utils';
import type {
  DigitalPolicy,
  DigitalAssetView,
  DigitalOffer,
  DigitalOrderView,
  DigitalLicenseView,
  DigitalPublisher,
  Page,
  DigitalCopyrightCaseView,
  Project,
} from '@yingbaba/types';
import { DigitalCheckout } from './DigitalCheckout';
import { DigitalSupplier } from './DigitalSupplier';
import { DigitalPurchaseProject } from './DigitalPurchaseProject';

export const digitalStatus: Record<string, string> = {
  draft: '草稿',
  submitted: '审核中',
  approved: '已审核',
  rejected: '已拒绝',
  published: '销售中',
  delisted: '已下架',
  pending: '待处理',
  active: '有效',
  suspended: '已暂停',
  paid: '已支付',
  payment_review: '到账待核查',
  cancelled: '已取消',
  refunded: '已退款',
  expired: '已过期',
  requested: '待审核',
  processing: '出款处理中',
  held: '版权核查中',
  received: '通知待受理',
  dismissed: '已解除',
  upheld: '维持处理',
  revoked: '已撤销',
};
export const digitalCategory: Record<string, string> = {
  video: '视频',
  image: '图片',
  audio: '音频',
  template: '后期模板',
  model3d: '三维素材',
};
export const fieldStyle = { display: 'grid', gap: 14 };
export function DigitalResources() {
  const session = useSession();
  return <DigitalResourceScope key={`${session.data?.account.id}:${orgID()}`} />;
}
function DigitalResourceScope() {
  const session = useSession();
  const qc = useQueryClient();
  const [urlParams, setURLParams] = useSearchParams();
  const tab = ['market', 'purchases', 'supplier', 'rights'].includes(urlParams.get('tab') || '')
    ? urlParams.get('tab')!
    : 'market';
  function setTab(value: string) {
    setURLParams((p) => {
      value === 'market' ? p.delete('tab') : p.set('tab', value);
      p.delete('asset');
      p.delete('license');
      return p;
    });
  }
  function updateParams(values: Record<string, string>, replace = false) {
    setURLParams(
      (p) => {
        for (const [key, value] of Object.entries(values))
          value ? p.set(key, value) : p.delete(key);
        return p;
      },
      { replace },
    );
  }
  const readPage = (key: string) =>
    Math.min(10000, Math.max(1, Math.floor(Number(urlParams.get(key)) || 1)));
  const page = readPage('market_page'),
    orderPage = readPage('purchase_page'),
    rightsPage = readPage('rights_page');
  const category = Object.hasOwn(digitalCategory, urlParams.get('category') || '')
    ? urlParams.get('category')!
    : '';
  const query = urlParams.get('q') || '',
    orderQuery = urlParams.get('purchase_q') || '';
  const owner =
    (urlParams.get('owner') || urlParams.get('owner_type')) === 'organization'
      ? 'organization'
      : 'account';
  const orderStatus = [
    'pending',
    'paid',
    'payment_review',
    'expired',
    'cancelled',
    'refunded',
  ].includes(urlParams.get('purchase_status') || '')
    ? urlParams.get('purchase_status')!
    : '';
  const [search, setSearch] = useState(query),
    [orderSearch, setOrderSearch] = useState(orderQuery);
  useEffect(() => setSearch(query), [query]);
  useEffect(() => setOrderSearch(orderQuery), [orderQuery]);
  function setPage(v: number) {
    updateParams({ market_page: v > 1 ? String(v) : '' });
  }
  function setOrderPage(v: number) {
    updateParams({ purchase_page: v > 1 ? String(v) : '' });
  }
  function setOwner(v: string) {
    updateParams({ owner: v === 'organization' ? v : '', owner_type: '', purchase_page: '' });
  }
  const policy = useData<DigitalPolicy>('/digital/policy');
  const focusedID = urlParams.get('asset') || '';
  const focused = useData<DigitalAssetView>(`/digital/assets/${focusedID}`, !!focusedID);
  const catalog = useData<Page<DigitalAssetView>>(
    `/digital/assets?page=${page}&category=${encodeURIComponent(category)}&q=${encodeURIComponent(query)}`,
    tab === 'market' && !focusedID,
  );
  const assets = focusedID
    ? {
        ...focused,
        data: focused.data
          ? { items: [focused.data], total: 1, page: 1, page_size: 18 }
          : undefined,
      }
    : catalog;
  const ownerID = owner === 'account' ? session.data?.account.id : orgID();
  const orders = useData<Page<DigitalOrderView>>(
    `/digital/orders?owner_type=${owner}&owner_id=${ownerID || ''}&page=${orderPage}&q=${encodeURIComponent(orderQuery)}&status=${orderStatus}`,
    tab === 'purchases' && !!ownerID,
  );
  const cases = useData<Page<DigitalCopyrightCaseView>>(
    `/digital/copyright-cases?page=${rightsPage}`,
    tab === 'rights',
  );
  const [selection, setSelection] = useState<{
    asset: DigitalAssetView;
    offer: DigitalOffer;
  } | null>(null);
  const [license, setLicense] = useState<DigitalLicenseView | null>(null);
  const [purchaseProject, setPurchaseProject] = useState<Project | null>(null);
  const [report, setReport] = useState<DigitalAssetView | null>(null);
  const [checkout, setCheckout] = useState<{ orderID?: string; paymentID?: string } | null>(() => {
    const id = new URLSearchParams(window.location.search).get('digital_payment') || '';
    return /^YBA[0-9A-HJKMNP-TV-Z]{26}$/.test(id) ? { paymentID: id.slice(3) } : null;
  });
  const openedAsset = useRef('');
  useEffect(() => {
    const licenseCode = urlParams.get('license');
    if (!focused.data || !licenseCode || openedAsset.current === focusedID || !focused.data.can_buy)
      return;
    const offer = focused.data.prices.find((p) => p.code === licenseCode);
    if (offer) {
      openedAsset.current = focusedID;
      setSelection({ asset: focused.data, offer });
    }
  }, [focused.data, focusedID, urlParams]);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState('');
  const pending = useRef(false);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState('');
  const refresh = () =>
    qc.invalidateQueries({
      predicate: (q) => typeof q.queryKey[0] === 'string' && q.queryKey[0].includes('/digital/'),
    });
  async function purchase(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!selection || pending.current) return;
    pending.current = true;
    setBusy(true);
    setError(null);
    const f = new FormData(e.currentTarget);
    const typ = String(f.get('owner_type')) as 'account' | 'organization';
    const id = typ === 'account' ? session.data?.account.id : orgID();
    const payload = {
      asset_id: selection.asset.asset.id,
      revision_id: selection.asset.release.id,
      owner_type: typ,
      owner_id: id,
      license_code: selection.offer.code,
      licensee_name: String(f.get('licensee_name')).trim(),
      project_name: String(f.get('project_name')).trim(),
      project_id: purchaseProject?.id || '',
      agreement_version: policy.data?.license_version,
      instant_delivery_consent: f.get('delivery') === 'on',
      authority_confirmed: f.get('authority') === 'on',
    };
    const scope = `digital-order:${session.data?.account.id}:${typ}:${id}:${payload.asset_id}`;
    try {
      const key = await memberCommandKey(scope, payload);
      const o = await post<DigitalOrderView>('/digital/orders', {
        ...payload,
        idempotency_key: key,
      });
      clearMemberCommand(scope);
      setSelection(null);
      setPurchaseProject(null);
      setCheckout({ orderID: o.id });
      setOwner(typ);
      await refresh();
    } catch (e) {
      setError(e);
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  async function viewLicense(id: string) {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError(null);
    try {
      setLicense(await api<DigitalLicenseView>(`/digital/orders/${id}/license`));
    } catch (e) {
      setError(e);
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  async function download(o: DigitalOrderView, id: string, name: string) {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setDownloading(id);
    setError(null);
    try {
      await downloadPrivateFile(`/digital/orders/${o.id}/files/${id}/download`, name);
    } catch (e) {
      setError(e);
    } finally {
      pending.current = false;
      setBusy(false);
      setDownloading('');
    }
  }
  function exportLicense() {
    if (!license) return;
    const c = license.contract;
    const text = `影爸爸数字资源交易授权凭证\n授权编号：${license.number}\n订单号：${license.order_id}\n资源：${c.title}\n版本：${c.revision_id}\n平台许可方：${c.platform_legal_name}\n权利人：${c.copyright_holder}\n被授权主体：${c.licensee_name}\n项目：${c.project_name}\n授权：${c.license.name}\n范围：${c.license.scope}\n限制：${c.license.restrictions}\n地域：${c.territory === 'CN' ? '中国大陆' : '全球'}\n期限：限具名项目永久使用，遵守合同和上游权利范围\n签发时间：${datetime(license.issued_at)}\n合同摘要：${license.contract_hash}\n\n本凭证不是政府版权登记、公证或权属担保。著作权不转让，未授予独家或源文件转售、AI训练权限。\n\n文件摘要：\n${c.files.map((f) => `${f.name} | SHA-256 ${f.sha256}`).join('\n')}\n\n冻结的许可条款：\n${c.terms || ''}`;
    const u = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = u;
    a.download = `授权凭证-${license.number}.txt`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(u), 1000);
  }
  async function submitReport(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!report || pending.current) return;
    pending.current = true;
    setBusy(true);
    setError(null);
    const f = new FormData(e.currentTarget);
    try {
      await post('/digital/copyright-cases', {
        asset_id: report.asset.id,
        claim: f.get('claim'),
        evidence: f.get('evidence'),
      });
      setReport(null);
      setNotice('版权通知已提交，平台将核查并转送；提交通知不等于已认定侵权。');
      await refresh();
    } catch (e) {
      setError(e);
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  return (
    <div className="digital-resources">
      <PageTitle
        title="数字资源"
        description="按用途寻找素材、管理授权与下载，也可以发布自己的创作作品。"
      />
      <nav className="tabs work-section-tabs" aria-label="数字资源功能">
        {[
          ['market', '资源市场'],
          ['purchases', '我的购买'],
          ['supplier', '发布与收益'],
          ['rights', '版权通知'],
        ].map(([k, label]) => (
          <button
            type="button"
            key={k}
            className={tab === k ? 'active' : ''}
            aria-current={tab === k ? 'page' : undefined}
            onClick={() => {
              setTab(k);
              setError(null);
            }}
          >
            {label}
          </button>
        ))}
      </nav>
      <ErrorNotice error={error} />
      {notice && <Notice>{notice}</Notice>}
      {tab === 'market' && (
        <>
          {focusedID && (
            <Button
              variant="ghost"
              onClick={() => {
                updateParams({ asset: '', license: '' });
                openedAsset.current = '';
              }}
            >
              浏览全部素材
            </Button>
          )}
          <Notice>
            购买的是使用许可，不是著作权转让。请选择实际使用主体和项目；会员不自动包含下载或商用授权。
          </Notice>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              updateParams({ q: search.trim(), market_page: '', asset: '', license: '' });
            }}
            className="digital-work-search"
            role="search"
            aria-label="查找素材"
          >
            <Field label="资源类别">
              <Select
                value={category}
                onChange={(e) => {
                  updateParams({ category: e, market_page: '', asset: '', license: '' });
                }}
              >
                <option value="">全部类别</option>
                {Object.entries(digitalCategory).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="关键词" htmlFor="digital-work-keyword">
              <input
                id="digital-work-keyword"
                className="input"
                aria-label="资源关键词"
                placeholder="搜索素材、标签或制作软件"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                maxLength={200}
              />
            </Field>
            <Button type="submit" variant="secondary">
              搜索
            </Button>
          </form>
          <ErrorNotice error={assets.error} />
          {assets.isLoading ? (
            <p>正在加载资源…</p>
          ) : (
            assets.isSuccess && (
              <>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,280px),1fr))',
                    gap: 18,
                  }}
                >
                  {assets.data?.items.map((v) => (
                    <section
                      className="card digital-work-asset"
                      key={v.asset.id}
                      style={{ padding: 18, overflowWrap: 'anywhere' }}
                    >
                      <h2>{v.release.title}</h2>
                      <p className="muted">
                        {digitalCategory[v.release.category]} · 版本 {v.release.version}
                        {v.rights.ai_generated ? ' · 含 AIGC，已披露来源' : ''}
                      </p>
                      {digitalPreviews(v.files, v.release.category)
                        .slice(0, 1)
                        .map((f) => {
                          const url = `${apiOrigin()}/api/v1/digital/assets/${v.asset.id}/preview/${f.id}`;
                          return f.mime.startsWith('image/') ? (
                            <img
                              key={f.id}
                              src={url}
                              alt={`${v.release.title}预览`}
                              style={{ width: '100%', maxHeight: 220, objectFit: 'contain' }}
                            />
                          ) : f.mime.startsWith('video/') ? (
                            <video
                              key={f.id}
                              controls
                              preload="none"
                              src={url}
                              style={{ width: '100%' }}
                            />
                          ) : (
                            <audio
                              key={f.id}
                              controls
                              preload="none"
                              src={url}
                              style={{ maxWidth: '100%' }}
                            />
                          );
                        })}
                      <p>{v.release.description}</p>
                      <p>{v.release.specifications}</p>
                      {v.release.software && <p>软件与依赖：{v.release.software}</p>}
                      <p>
                        权利人：{v.rights.holder_name} ·{' '}
                        {v.rights.territory === 'CN' ? '中国大陆' : '全球'}
                      </p>
                      {v.prices.map((offer) => (
                        <div
                          key={offer.code}
                          className="row"
                          style={{
                            justifyContent: 'space-between',
                            gap: 10,
                            flexWrap: 'wrap',
                            marginTop: 12,
                          }}
                        >
                          <span>
                            {policy.data?.licenses.find((l) => l.code === offer.code)?.name ||
                              offer.code}{' '}
                            · {money(offer.amount_minor)}
                          </span>
                          <Button
                            disabled={!v.can_buy || !policy.data?.sales_enabled}
                            onClick={() => {
                              setError(null);
                              setSelection({ asset: v, offer });
                            }}
                          >
                            选择授权
                          </Button>
                        </div>
                      ))}
                      {!v.can_buy && (
                        <p className="muted">此素材暂未开放购买，可先预览并了解授权范围。</p>
                      )}
                      <Button size="sm" variant="secondary" onClick={() => setReport(v)}>
                        版权通知
                      </Button>
                    </section>
                  ))}
                </div>
                {!assets.data?.items.length && <p>暂无匹配的已审核资源。</p>}
                <Pagination
                  page={page}
                  total={assets.data?.total || 0}
                  size={assets.data?.page_size || 18}
                  setPage={setPage}
                />
              </>
            )
          )}
        </>
      )}
      {tab === 'purchases' && (
        <>
          <div className="digital-purchase-owners" aria-label="购买主体">
            <Button
              variant={owner === 'account' ? 'primary' : 'secondary'}
              onClick={() => {
                setOwner('account');
              }}
            >
              个人购买
            </Button>
            <Button
              variant={owner === 'organization' ? 'primary' : 'secondary'}
              disabled={!orgID()}
              onClick={() => {
                setOwner('organization');
              }}
            >
              当前组织购买
            </Button>
          </div>
          <form
            className="digital-purchase-filter"
            role="search"
            aria-label="查找购买记录"
            onSubmit={(e) => {
              e.preventDefault();
              updateParams({ purchase_q: orderSearch.trim(), purchase_page: '' });
            }}
          >
            <input
              className="input"
              aria-label="购买记录关键词"
              placeholder="搜索素材、项目、授权主体或订单号"
              maxLength={200}
              value={orderSearch}
              onChange={(e) => setOrderSearch(e.target.value)}
            />
            <Select
              aria-label="购买状态"
              value={orderStatus}
              onChange={(v) => updateParams({ purchase_status: v, purchase_page: '' })}
            >
              <option value="">全部状态</option>
              <option value="pending">待付款</option>
              <option value="payment_review">到账核查中</option>
              <option value="paid">已支付</option>
              <option value="expired">已过期</option>
              <option value="cancelled">已取消</option>
              <option value="refunded">已退款</option>
            </Select>
            <Button type="submit" variant="secondary">
              搜索
            </Button>
          </form>
          <ErrorNotice error={orders.error} />
          {orders.isLoading ? (
            <p>正在查询购买记录…</p>
          ) : (
            orders.isSuccess && (
              <>
                {orders.data.items.map((o) => (
                  <section
                    className="panel digital-purchase-card"
                    key={o.id}
                    style={{ padding: 18, marginTop: 14, overflowWrap: 'anywhere' }}
                  >
                    <header className="digital-purchase-header">
                      <div>
                        <h2>{o.title}</h2>
                        <p>
                          {o.status === 'pending' ? '待付款' : digitalStatus[o.status] || o.status}{' '}
                          · {o.contract.license.name}
                        </p>
                      </div>
                      <strong className="digital-purchase-amount">{money(o.amount_minor)}</strong>
                    </header>
                    <p>
                      被授权主体：{o.licensee_name} · 项目：{o.project_name}
                    </p>
                    <p className="small muted">订单号：{o.id}</p>
                    <details>
                      <summary>成交授权范围</summary>
                      <p>{o.contract.license.scope}</p>
                      <p>{o.contract.license.restrictions}</p>
                      <p>权利人：{o.contract.copyright_holder}</p>
                      <p>
                        地域：{o.contract.territory === 'CN' ? '中国大陆' : '全球'} · 合同摘要：
                        {o.contract_hash}
                      </p>
                    </details>
                    <div className="row" style={{ gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
                      {['pending', 'payment_review', 'expired'].includes(o.status) && (
                        <Button onClick={() => setCheckout({ orderID: o.id })}>查看收银台</Button>
                      )}
                      <OrderSupport kind="digital" orderID={o.id} disabled={busy} />
                      {['paid', 'refunded'].includes(o.status) && (
                        <Button
                          disabled={busy}
                          onClick={() => void viewLicense(o.id)}
                          variant="secondary"
                        >
                          授权凭证
                        </Button>
                      )}
                      {o.status === 'paid' &&
                        o.project_id &&
                        o.owner_type === 'organization' &&
                        o.owner_id === orgID() &&
                        ['owner', 'admin'].includes(
                          session.data?.organizations.find((org) => org.id === orgID())?.role || '',
                        ) && (
                          <Link
                            className="btn btn-secondary"
                            to={digitalProjectAssetPath(o.project_id, o.id)}
                          >
                            前往项目添加素材
                          </Link>
                        )}
                      {o.status === 'paid' &&
                        o.contract.files.map((f) => (
                          <Button
                            key={f.id}
                            disabled={busy}
                            loading={downloading === f.id}
                            onClick={() => void download(o, f.id, f.name)}
                          >
                            下载 {f.name}
                          </Button>
                        ))}
                    </div>
                  </section>
                ))}
                {!orders.data.items.length && (
                  <Empty
                    title={orderQuery || orderStatus ? '没有找到相关购买记录' : '暂无购买记录'}
                    description={
                      orderQuery || orderStatus
                        ? '试试其他关键词或购买状态。'
                        : '购买数字素材后，可在这里查看授权凭证与下载文件。'
                    }
                    action={
                      orderQuery || orderStatus || orderPage > 1 ? (
                        <Button
                          variant="secondary"
                          onClick={() =>
                            updateParams({ purchase_q: '', purchase_status: '', purchase_page: '' })
                          }
                        >
                          查看全部购买记录
                        </Button>
                      ) : (
                        <Button variant="secondary" onClick={() => setTab('market')}>
                          浏览数字素材
                        </Button>
                      )
                    }
                  />
                )}
                <Pagination
                  page={orderPage}
                  total={orders.data.total}
                  size={orders.data.page_size}
                  setPage={setOrderPage}
                />
              </>
            )
          )}
        </>
      )}
      {tab === 'supplier' && <DigitalSupplier />}
      {tab === 'rights' && (
        <>
          <Notice>
            受理、反通知和决定分别留痕。争议核查不是对著作权归属的最终司法认定；被冻结下载的历史许可证据仍可保留。
          </Notice>
          <ErrorNotice error={cases.error} />
          {cases.isLoading ? (
            <p>正在读取通知…</p>
          ) : (
            cases.data?.items.map((c) => (
              <section className="panel" key={c.id} style={{ padding: 18, marginTop: 14 }}>
                <strong>{digitalStatus[c.status] || c.status}</strong>
                <p>{c.claim}</p>
                <p>{c.decision}</p>
                {c.counter_statement && <p>发布者声明：{c.counter_statement}</p>}
                {c.can_counter && ['received', 'held'].includes(c.status) && (
                  <form
                    onSubmit={async (e) => {
                      e.preventDefault();
                      if (pending.current) return;
                      pending.current = true;
                      setBusy(true);
                      const form = new FormData(e.currentTarget);
                      setError(null);
                      try {
                        await post(`/digital/copyright-cases/${c.id}/counter`, {
                          expected_version: c.version,
                          statement: form.get('statement'),
                        });
                        await refresh();
                        setNotice('发布者声明已提交，平台将继续核查。');
                      } catch (e) {
                        setError(e);
                      } finally {
                        pending.current = false;
                        setBusy(false);
                      }
                    }}
                  >
                    <label className="field">
                      发布者不侵权声明及证据
                      <textarea
                        name="statement"
                        disabled={busy}
                        minLength={15}
                        maxLength={10000}
                        required
                      />
                    </label>
                    <Button loading={busy}>提交发布者反通知</Button>
                  </form>
                )}
              </section>
            ))
          )}
          {cases.isSuccess && !cases.data?.items.length && (
            <Empty title="暂无版权通知" description="你提交或收到的版权核查进展会在这里显示。" />
          )}
          <Pagination
            page={rightsPage}
            total={cases.data?.total || 0}
            size={cases.data?.page_size || 24}
            setPage={(v) => updateParams({ rights_page: v > 1 ? String(v) : '' })}
          />
        </>
      )}
      {selection && (
        <Modal
          open
          title="确认资源授权"
          onOpenChange={(o) => {
            if (!o && !busy) {
              setSelection(null);
              setPurchaseProject(null);
            }
          }}
        >
          <form onSubmit={purchase} style={fieldStyle}>
            <p>
              {selection.asset.release.title} · {money(selection.offer.amount_minor)}
            </p>
            <p>{policy.data?.licenses.find((l) => l.code === selection.offer.code)?.scope}</p>
            <p>
              {policy.data?.licenses.find((l) => l.code === selection.offer.code)?.restrictions}
            </p>
            <details>
              <summary>阅读数字资源许可及退换条款</summary>
              <p style={{ whiteSpace: 'pre-wrap' }}>{policy.data?.license_terms}</p>
            </details>
            <input
              type="hidden"
              name="owner_type"
              value={selection.offer.code === 'personal' ? 'account' : 'organization'}
            />
            <p>
              采购归属：{selection.offer.code === 'personal' ? '本人账号' : '当前已认证组织'}
              ；不支持通过个人授权替企业或客户使用。
            </p>
            <label className="field">
              最终被授权主体
              <input
                className="input"
                name="licensee_name"
                required
                minLength={2}
                maxLength={120}
                defaultValue={
                  selection.offer.code === 'personal'
                    ? session.data?.account.name
                    : session.data?.organizations.find((o) => o.id === orgID())?.name
                }
              />
            </label>
            {selection.offer.code !== 'personal' &&
              ['owner', 'admin'].includes(
                session.data?.organizations.find((o) => o.id === orgID())?.role || '',
              ) && <DigitalPurchaseProject onSelect={setPurchaseProject} />}
            <label className="field">
              具名成品项目
              <input
                key={purchaseProject?.id || 'manual-project'}
                className="input"
                name="project_name"
                required
                minLength={2}
                maxLength={200}
                defaultValue={purchaseProject?.title || ''}
                readOnly={!!purchaseProject}
              />
            </label>
            <label>
              <input type="checkbox" name="authority" required />
              我确认主体及用途真实，并有权代表该主体接受许可。
            </label>
            <label>
              <input type="checkbox" name="delivery" required />
              我同意数字内容即时交付并已了解退换说明；侵权、质量、无法交付等法定权利不受排除。
            </label>
            <Notice>
              付款后锁定本次版本、授权和项目。著作权不转让，源文件转售和 AI 训练不在授权内。
            </Notice>
            <ErrorNotice error={error} />
            <Button
              disabled={
                busy ||
                (selection.offer.code !== 'personal' &&
                  !['owner', 'admin'].includes(
                    session.data?.organizations.find((o) => o.id === orgID())?.role || '',
                  ))
              }
            >
              确认订单并付款
            </Button>
          </form>
        </Modal>
      )}
      {license && (
        <Modal
          open
          title="数字资源授权凭证"
          onOpenChange={(o) => {
            if (!o) setLicense(null);
          }}
        >
          <p>授权编号：{license.number}</p>
          <p>
            {license.contract.title} · {license.contract.license.name}
          </p>
          <p>授权主体：{license.contract.licensee_name}</p>
          <p>项目：{license.contract.project_name}</p>
          <p>{license.contract.license.scope}</p>
          <p>{license.contract.license.restrictions}</p>
          <p>
            状态：{digitalStatus[license.status]}
            {license.suspended ? '，下载正在版权核查中' : ''}
          </p>
          <Notice>这是平台交易授权凭证，不是政府版权登记、公证或权属担保。</Notice>
          <Button onClick={exportLicense}>保存授权凭证</Button>
        </Modal>
      )}
      {report && (
        <Modal
          open
          title="提交版权通知"
          onOpenChange={(o) => {
            if (!o && !busy) setReport(null);
          }}
        >
          <form onSubmit={submitReport} style={fieldStyle}>
            <p>{report.release.title}</p>
            <label className="field">
              权利人身份、作品位置及具体侵权说明
              <textarea name="claim" required minLength={15} maxLength={10000} />
            </label>
            <label className="field">
              初步权属证据和联系资料
              <textarea name="evidence" required minLength={10} maxLength={10000} />
            </label>
            <ErrorNotice error={error} />
            <Button disabled={busy}>提交供平台核查</Button>
          </form>
        </Modal>
      )}
      {checkout && (
        <DigitalCheckout
          {...checkout}
          onClose={() => {
            setCheckout(null);
            openedAsset.current = '';
            updateParams({ digital_payment: '', license: '' }, true);
          }}
          onChanged={() => void refresh()}
          onViewPurchase={(order) => {
            setCheckout(null);
            openedAsset.current = '';
            updateParams({
              tab: 'purchases',
              owner: order.owner_type,
              owner_type: '',
              purchase_q: order.id,
              purchase_page: '',
              purchase_status: '',
              digital_payment: '',
              license: '',
              asset: '',
            });
          }}
        />
      )}
    </div>
  );
}
export function Pagination({
  page,
  total,
  size,
  setPage,
}: {
  page: number;
  total: number;
  size: number;
  setPage: (n: number) => void;
}) {
  if (total <= size && page <= 1) return null;
  return (
    <div className="row" style={{ gap: 12, marginTop: 18, flexWrap: 'wrap' }}>
      <Button variant="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>
        上一页
      </Button>
      <span>
        第 {page} 页，共 {total} 条
      </span>
      <Button variant="secondary" disabled={page * size >= total} onClick={() => setPage(page + 1)}>
        下一页
      </Button>
    </div>
  );
}
